import { Button, Stack } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import useFetch from "@entrilia/shared/hooks/useFetch";
import ExcelIcon from "@entrilia/shared/icons/fileFormats/ExcelIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../api/adminApi";
import type { DataImportOption } from "../../../api/types/dataImportTypes";
import type { ObjectClassDefinition } from "../../../api/types/objectTypes";
import useClientContext from "../../../context/useClientContext";
import { pageRoutes } from "../../../routes";
import storage from "../../../storage/storage";
import { getReferrerLocationState } from "../../../utilities/navigationHelper";
import { FilterContextProvider } from "../../common/filters/FilterContextProvider";
import FiltersPanel from "../../common/filters/FiltersPanel";
import { filterRecords, getInitialFilterState, getVisibleColumnsAndFiltersIds } from "../../common/filters/filterState";
import { getEntityFilterDefinitions } from "../entityFieldFilterDefinitions";
import EntitiesGrid from "./EntitiesGrid";

interface Props {
  objectClassDefinition: ObjectClassDefinition;
  dataImportOptions: DataImportOption[];
  showLogos?: boolean;
  getDetailsPath: (id: string) => string;
}

const EntitiesList = ({ objectClassDefinition, dataImportOptions, showLogos, getDetailsPath }: Props) => {
  const { clientCode, dictionaries } = useClientContext();
  const location = useLocation();
  const navigate = useNavigate();

  const filterId = useMemo(
    () => `${clientCode}_${objectClassDefinition.objectType}_filter_v2`,
    [clientCode, objectClassDefinition.objectType]
  );

  const [filterState, setFilterState] = useState(() => {
    const { filterDefinitions, columnDefinitions, searchDefinition } = getEntityFilterDefinitions(
      objectClassDefinition.fields,
      objectClassDefinition.defaultGridFieldIds,
      objectClassDefinition.showTimestampsInGridByDefault,
      [
        objectClassDefinition.displayFields.primaryFieldId,
        objectClassDefinition.displayFields.secondaryFieldId ?? "",
      ].filter(Boolean),
      dictionaries
    );

    return getInitialFilterState(filterId, filterDefinitions, [], columnDefinitions, searchDefinition);
  });

  const getObjects = useCallback(async () => {
    const fieldIds = getVisibleColumnsAndFiltersIds(filterState);
    return adminApi.searchObjects(objectClassDefinition.objectType, { fieldIds, includeLookupObjects: true });
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [filterState.visibleColumns, filterState.visibleFilters, objectClassDefinition.objectType]);

  const [objectsResp, objectsError, { isFetching: isFetchingObjects, fetch: fetchObjects }] = useFetch(getObjects);

  if (objectsError) {
    logError(objectsError, "[EntitiesList]");
    storage.clearFilterState(filterId);
    return <DataLoadingFailed />;
  }

  const handleImportFromCsv = () => {
    navigate(`/${clientCode}/${pageRoutes.settings}/${pageRoutes.importData}/${pageRoutes.importCsvData}`, {
      state: {
        ...getReferrerLocationState(location),
        preSelectedObjectType: objectClassDefinition.objectType,
      },
    });
  };

  const hasPermissionsForDataImport = (dataImportOptions ?? []).some(
    (option) => option.canImport && option.objectType === objectClassDefinition.objectType
  );

  const hasAccessToCsvImport =
    hasPermissionsForDataImport && objectClassDefinition.supportedFeatures.includes("DataImport");

  const isLoading = isFetchingObjects || objectsResp === undefined;

  const entities = objectsResp?.items ?? [];
  const filteredEntities = filterRecords(filterState, entities);

  return (
    <Stack spacing={2} sx={{ height: "100%", px: 3, pb: 1, pt: 2.5 }}>
      <FilterContextProvider
        filterState={filterState}
        setFilterState={setFilterState}
        allRowsForSelectOptions={entities}
      >
        <FiltersPanel
          totalCount={entities.length}
          recordCount={filteredEntities.length}
          isLoading={isLoading}
          hasColumnSelection
          onRefresh={fetchObjects}
        >
          {hasAccessToCsvImport && (
            <Button
              variant="outlined"
              color="secondary"
              disabled={isLoading}
              startIcon={<ExcelIcon />}
              sx={{ minWidth: "auto", whiteSpace: "nowrap" }}
              onClick={handleImportFromCsv}
            >
              Import from CSV
            </Button>
          )}
        </FiltersPanel>
        <EntitiesGrid
          entities={filteredEntities}
          fields={objectClassDefinition.fields}
          isLoading={isLoading}
          showLogos={showLogos}
          getDetailsPath={getDetailsPath}
        />
      </FilterContextProvider>
    </Stack>
  );
};

export default EntitiesList;
